import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { notUndefined } from '@ppwcode/js-ts-oddsandends/lib/conditional-assert';
import { produce } from 'immer';
import { Observable, Subscription } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { ProfileState } from '../../../../profile/store/profile.state';
import { NavigateService } from '../../../../shared/services/navigate.service';
import { AuthState } from '../../../../shared/store/auth.state';
import { NgxsStateFacade } from '../../../../shared/store/ngxs-state-facade';
import { TvhMachineType } from '../../machine/constants/machine-type.constant';
import { Machine } from '../../machine/models/machine.model';
import { LocationDetailService } from '../services/location-detail.service';

export interface LocationDetailModel {
  locationId?: number;
  machines: Array<Machine>;
  availableMachineTypes: Array<TvhMachineType>;
}

export class AddCredit {
  static readonly type = '[LocationDetail] Add Credit';
}

export class CancelActivityStart {
  static readonly type = '[LocationDetail] Cancel Activity Start';
}

export class StartActivity {
  static readonly type = '[LocationDetail] Start Activity';
}

export class ShowLocationMachines {
  static readonly type = '[LocationDetail] Show Location Machines';
  constructor(public machineType: TvhMachineType) {}
}

export class SetMachinesInfoForLocation {
  static readonly type = '[LocationDetail] Set Machines Info For Location';
  constructor(
    public id: number,
    public machines: Array<Machine>,
  ) {}
}

@State<LocationDetailModel>({
  name: 'locationDetail',
  defaults: {
    locationId: undefined,
    machines: [],
    availableMachineTypes: [],
  },
})
@Injectable()
export class LocationDetailState extends NgxsStateFacade<LocationDetailModel> {
  private locationRefresh: Subscription;
  private readonly restrictedMachineTypes: Array<TvhMachineType> = [TvhMachineType.DOOR, TvhMachineType.LIGHT];

  constructor(
    store: Store,
    private readonly authState: AuthState,
    private readonly profile: ProfileState,
    private readonly navigate: NavigateService,
    private readonly service: LocationDetailService,
  ) {
    super(store, 'locationDetail');
  }

  @Selector()
  static locationId(state: LocationDetailModel): number | undefined {
    return state.locationId;
  }

  @Selector()
  static machines(state: LocationDetailModel): Array<Machine> {
    return state.machines;
  }

  @Selector()
  static availableMachineTypes(state: LocationDetailModel): Array<TvhMachineType> {
    return state.availableMachineTypes;
  }

  // --- Public API ---

  get locationId(): number | undefined {
    return this.store.selectSnapshot(LocationDetailState.locationId);
  }

  get machines(): Array<Machine> {
    return [...this.store.selectSnapshot(LocationDetailState.machines)];
  }

  get myMachines(): Array<Machine> {
    const currentUserId = this.profile.currentUserId;
    return [...this.machines.filter((m: Machine) => m.customerId === currentUserId)];
  }

  get availableMachineTypes(): Array<TvhMachineType> {
    return [...this.store.selectSnapshot(LocationDetailState.availableMachineTypes)];
  }

  addCredit(): void {
    this.store.dispatch(new AddCredit());
  }

  cancelActivityStart(): void {
    this.store.dispatch(new CancelActivityStart());
  }

  startActivity(): void {
    this.store.dispatch(new StartActivity());
  }

  showLocationMachines(type: TvhMachineType): void {
    this.store.dispatch(new ShowLocationMachines(type));
  }

  setMachinesInfoForLocation(id: number, machines: Array<Machine>): void {
    this.store.dispatch(new SetMachinesInfoForLocation(id, machines));
  }

  ngxsAfterBootstrap(): void {
    if (this.locationRefresh) {
      this.locationRefresh.unsubscribe();
    }
    this.locationRefresh = this.authState.refreshTrigger$
      .pipe(
        filter(() => !!this.locationId),
        switchMap(() => this.service.getMachinesInfoForLocation(this.locationId, true)),
      )
      .subscribe((machines: Array<Machine>) => {
        this.setMachinesInfoForLocation(this.locationId, machines);
      });
  }

  // --- Action Handlers ---

  @Action(AddCredit)
  addCreditHandler() {
    this.navigate.toAddCredit();
  }

  @Action(CancelActivityStart)
  cancelActivityStartHandler() {
    this.navigate.toLocationById(this.snapshot.locationId);
  }

  @Action(StartActivity)
  startActivityHandler() {
    this.navigate.toStartActivity(this.snapshot.locationId);
  }

  @Action(ShowLocationMachines)
  showLocationMachinesHandler(ctx: StateContext<LocationDetailModel>, action: ShowLocationMachines) {
    this.navigate.toLocationMachines(notUndefined(ctx.getState().locationId), action.machineType);
  }

  @Action(SetMachinesInfoForLocation)
  setMachinesInfoForLocationHandler(ctx: StateContext<LocationDetailModel>, action: SetMachinesInfoForLocation) {
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        draft.locationId = action.id;
        draft.machines = action.machines;
        draft.availableMachineTypes = [];
        for (const type of Object.keys(TvhMachineType)) {
          if (
            !this.restrictedMachineTypes.includes(type as TvhMachineType) &&
            !!action.machines.find((m: Machine) => m.type === type)
          ) {
            draft.availableMachineTypes.push(type as TvhMachineType);
          }
        }
      }),
    );
  }
}
